import { useCallback, useEffect, useRef } from "react";
import {
  bootstrapFirstUserExperience,
  bootstrapReturningUserThread,
} from "../api/message";
import {
  firstUserMessage,
  firstUserPlaceholderText,
} from "../utils/constants";
import { readRememberedActiveChatThread } from "../utils/chat_thread_persistence";
import { reconcileFinalBotMessage } from "../pages/chatMessageReconciliation";

const FIRST_USER_BOOTSTRAP_QUESTIONS = [
  "Best Tech Stock?",
  "Crypto To Buy?",
];

const RETURNING_USER_RECOVERY_DELAYS_MS = [12000, 30000];

const isTempChatThreadUuid = (threadUuid) =>
  String(threadUuid || "").startsWith("temp-");

const hasCompletedBotMessage = (messages) =>
  Array.isArray(messages) &&
  messages.some(
    (message) =>
      message?.sender === "bot" && String(message?.message || "").trim(),
  );

const useChatBootstrapThreads = ({
  activeChatThreadPersistenceScope,
  activeThread,
  activeThreadRef,
  ensureThreadSubscription,
  firstUserBootstrapAttemptedRef,
  firstUserBootstrapInFlightRef,
  freshResponseFlow,
  hasFreshRequestedPersistedChatThread,
  hydrateThreadMessages,
  loadingMessageThreads,
  loadingUserData,
  messageThreads,
  messageThreadsOwnerRef,
  processedAutoQuestionRef,
  querystringQuestion,
  resolvedTempThreadMapRef,
  resolveTempIdRef,
  returningBootstrapAttemptedRef,
  returningBootstrapInFlightRef,
  selectActiveThread,
  setInputPlaceholderText,
  setIsSecondaryExperience,
  setResponseFlowForThread,
  setSecondaryExperienceMessage,
  setSecondaryExperienceType,
  setShowLegacyFirstUserExperience,
  setThreadLiveTextOutputting,
  setThreadLoadingPhaseDetail,
  setThreadLoadingPhaseMessage,
  setThreadLoadingResponse,
  setThreadPreventQuestionInput,
  setThreadShowLoadingWaitingMessage,
  setThreads,
  updateThreadMessages,
  userData,
}) => {
  const returningBootstrapThreadUuidRef = useRef(null);

  const clearReturningLoadingState = useCallback((threadUuid) => {
    setThreadLoadingResponse(threadUuid, false);
    setThreadShowLoadingWaitingMessage(threadUuid, 0);
    setThreadLoadingPhaseMessage(threadUuid, null);
    setThreadLoadingPhaseDetail(threadUuid, null);
    setThreadLiveTextOutputting(threadUuid, false);
    setThreadPreventQuestionInput(threadUuid, false);
  }, [
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadShowLoadingWaitingMessage,
  ]);

  useEffect(() => {
    const threadUuid = returningBootstrapThreadUuidRef.current;
    if (!threadUuid) return;
    if (activeThread?.threadUuid !== threadUuid) return;
    if (!hasCompletedBotMessage(messageThreads)) return;

    clearReturningLoadingState(threadUuid);
    returningBootstrapThreadUuidRef.current = null;
  }, [
    activeThread?.threadUuid,
    clearReturningLoadingState,
    messageThreads,
  ]);

  useEffect(() => {
    if (returningBootstrapAttemptedRef.current) return;
    if (returningBootstrapInFlightRef.current) return;
    if (loadingUserData || loadingMessageThreads) return;
    if (hasFreshRequestedPersistedChatThread) {
      returningBootstrapAttemptedRef.current = true;
      return;
    }
    if (readRememberedActiveChatThread(activeChatThreadPersistenceScope)) {
      returningBootstrapAttemptedRef.current = true;
      return;
    }
    if (querystringQuestion || processedAutoQuestionRef.current) {
      returningBootstrapAttemptedRef.current = true;
      return;
    }
    if (!userData?.user_id) return;

    returningBootstrapInFlightRef.current = true;
    let aborted = false;
    const recoveryTimers = [];

    const scheduleReturningRecovery = (threadUuid) => {
      if (!threadUuid || typeof window === "undefined") return;

      RETURNING_USER_RECOVERY_DELAYS_MS.forEach((delayMs) => {
        const timer = window.setTimeout(async () => {
          if (aborted) return;
          const messages = await hydrateThreadMessages?.(threadUuid, {
            force: true,
          });
          if (aborted || !hasCompletedBotMessage(messages)) return;
          clearReturningLoadingState(threadUuid);
          returningBootstrapThreadUuidRef.current = null;
        }, delayMs);
        recoveryTimers.push(timer);
      });
    };

    (async () => {
      const resp = await bootstrapReturningUserThread();
      if (!resp?.triggered || !resp?.threadUuid) return;

      const proactiveThread = {
        threadUuid: resp.threadUuid,
        title: resp.threadTitle || "Quick Follow-Up",
        isAdminTargeted: false,
      };

      setThreads((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return [
          proactiveThread,
          ...safePrev.filter(
            (thread) => thread?.threadUuid !== proactiveThread.threadUuid,
          ),
        ];
      });

      ensureThreadSubscription(resp.threadUuid, { setAsPrimary: true });
      messageThreadsOwnerRef.current = resp.threadUuid;
      updateThreadMessages(resp.threadUuid, [
        {
          sender: "bot",
          message: "",
        },
      ]);
      setThreadLoadingResponse(resp.threadUuid, true);
      setThreadShowLoadingWaitingMessage(resp.threadUuid, 0);
      setThreadLoadingPhaseMessage(resp.threadUuid, "Preparing your follow-up");
      setThreadLoadingPhaseDetail(resp.threadUuid, null);
      setThreadLiveTextOutputting(resp.threadUuid, false);
      setThreadPreventQuestionInput(resp.threadUuid, true);
      setResponseFlowForThread(resp.threadUuid, {
        ...freshResponseFlow(),
        review: false,
        line: false,
        text: false,
        question1: [false, "", null, false, null],
        question2: [false, "", null, false, null],
        regen: false,
      });
      returningBootstrapThreadUuidRef.current = resp.threadUuid;
      scheduleReturningRecovery(resp.threadUuid);
      selectActiveThread(proactiveThread);
    })().finally(() => {
      returningBootstrapAttemptedRef.current = true;
      returningBootstrapInFlightRef.current = false;
    });

    return () => {
      aborted = true;
      recoveryTimers.forEach((timer) => clearTimeout(timer));
    };
  }, [
    activeChatThreadPersistenceScope,
    clearReturningLoadingState,
    ensureThreadSubscription,
    hasFreshRequestedPersistedChatThread,
    hydrateThreadMessages,
    freshResponseFlow,
    loadingMessageThreads,
    loadingUserData,
    messageThreadsOwnerRef,
    processedAutoQuestionRef,
    querystringQuestion,
    returningBootstrapAttemptedRef,
    returningBootstrapInFlightRef,
    selectActiveThread,
    setResponseFlowForThread,
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadShowLoadingWaitingMessage,
    setThreads,
    updateThreadMessages,
    userData?.user_id,
  ]);

  useEffect(() => {
    if (firstUserBootstrapAttemptedRef.current) return;
    if (firstUserBootstrapInFlightRef.current) return;
    if (hasFreshRequestedPersistedChatThread) {
      firstUserBootstrapAttemptedRef.current = true;
      return;
    }
    if (querystringQuestion || processedAutoQuestionRef.current) {
      firstUserBootstrapAttemptedRef.current = true;
      return;
    }
    if (loadingUserData || loadingMessageThreads) return;
    if (userData?.user_id) {
      firstUserBootstrapAttemptedRef.current = true;
      return;
    }

    const threadUuid = activeThread?.threadUuid ?? null;
    if (!threadUuid || !isTempChatThreadUuid(threadUuid)) return;
    if (!userData?.current_session_id) return;
    if (Array.isArray(messageThreads) && messageThreads.some((m) => m?.sender === "human")) {
      firstUserBootstrapAttemptedRef.current = true;
      return;
    }

    firstUserBootstrapInFlightRef.current = true;
    setShowLegacyFirstUserExperience(false);
    setIsSecondaryExperience(false);
    setSecondaryExperienceType(null);
    setInputPlaceholderText(firstUserPlaceholderText);
    ensureThreadSubscription(threadUuid, { setAsPrimary: true });
    messageThreadsOwnerRef.current = threadUuid;
    updateThreadMessages(threadUuid, [
      {
        sender: "bot",
        message: "",
      },
    ]);
    setThreadLoadingResponse(threadUuid, true);
    setThreadShowLoadingWaitingMessage(threadUuid, 0);
    setThreadLoadingPhaseMessage(threadUuid, null);
    setThreadLoadingPhaseDetail(threadUuid, null);
    setThreadLiveTextOutputting(threadUuid, false);
    setThreadPreventQuestionInput(threadUuid, true);
    setResponseFlowForThread(threadUuid, {
      ...freshResponseFlow(),
      review: false,
      line: false,
      text: false,
      question1: [false, FIRST_USER_BOOTSTRAP_QUESTIONS[0], null, false, null],
      question2: [false, FIRST_USER_BOOTSTRAP_QUESTIONS[1], null, false, null],
      regen: false,
    });

    (async () => {
      const result = await bootstrapFirstUserExperience(threadUuid);
      const resolvedThreadUuid = result?.threadUuid || threadUuid;

      if (result?.threadUuid && result.threadUuid !== threadUuid) {
        resolveTempIdRef.current?.(
          threadUuid,
          result.threadUuid,
          result.threadTitle || "Start Here",
        );
      }

      if (result?.botMessage) {
        const firstUserQuestions = Array.isArray(result?.questionsPrepop)
          ? result.questionsPrepop
          : [];
        const questionOne = firstUserQuestions[0] || FIRST_USER_BOOTSTRAP_QUESTIONS[0];
        const questionTwo = firstUserQuestions[1] || FIRST_USER_BOOTSTRAP_QUESTIONS[1];
        updateThreadMessages(resolvedThreadUuid, (prev) => {
          const safePrev = Array.isArray(prev) ? prev : [];
          const lastMessage = safePrev[safePrev.length - 1];
          const finalMessage = reconcileFinalBotMessage(lastMessage, result.botMessage);
          if (safePrev.length === 0) return [finalMessage];
          return [...safePrev.slice(0, -1), finalMessage];
        });
        setResponseFlowForThread(resolvedThreadUuid, {
          ...freshResponseFlow(),
          review: false,
          line: false,
          text: false,
          question1: [true, questionOne, null, false, null],
          question2: [true, questionTwo, null, false, null],
          regen: false,
        });
        setThreadPreventQuestionInput(resolvedThreadUuid, false);
        return;
      }

      updateThreadMessages(resolvedThreadUuid, []);
      setShowLegacyFirstUserExperience(true);
      setIsSecondaryExperience(true);
      setSecondaryExperienceType("new-user");
      setSecondaryExperienceMessage(firstUserMessage);
    })().finally(() => {
      const activeUuid =
        resolvedTempThreadMapRef.current?.[threadUuid] || activeThreadRef.current?.threadUuid || threadUuid;
      setThreadLoadingResponse(activeUuid, false);
      setThreadShowLoadingWaitingMessage(activeUuid, 0);
      setThreadLoadingPhaseMessage(activeUuid, null);
      setThreadLoadingPhaseDetail(activeUuid, null);
      setThreadLiveTextOutputting(activeUuid, false);
      setThreadPreventQuestionInput(activeUuid, false);
      firstUserBootstrapAttemptedRef.current = true;
      firstUserBootstrapInFlightRef.current = false;
    });
  }, [
    activeThread?.threadUuid,
    activeThreadRef,
    ensureThreadSubscription,
    firstUserBootstrapAttemptedRef,
    firstUserBootstrapInFlightRef,
    freshResponseFlow,
    hasFreshRequestedPersistedChatThread,
    loadingMessageThreads,
    loadingUserData,
    messageThreads,
    messageThreadsOwnerRef,
    processedAutoQuestionRef,
    querystringQuestion,
    resolvedTempThreadMapRef,
    resolveTempIdRef,
    setInputPlaceholderText,
    setIsSecondaryExperience,
    setResponseFlowForThread,
    setSecondaryExperienceMessage,
    setSecondaryExperienceType,
    setShowLegacyFirstUserExperience,
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadShowLoadingWaitingMessage,
    updateThreadMessages,
    userData?.current_session_id,
    userData?.user_id,
  ]);
};

export default useChatBootstrapThreads;
